import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, BookOpen, Award, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const MentoringPage = () => {
  const navigate = useNavigate();

  const programFeatures = [
    {
      icon: Users,
      title: 'One-on-One Mentorship',
      description: 'Personalized guidance from experienced mentors who understand your unique journey and goals.',
    },
    {
      icon: Target,
      title: 'Goal Setting & Achievement',
      description: 'Strategic planning sessions to define, track, and accomplish meaningful life and career objectives.',
    },
    {
      icon: BookOpen,
      title: 'Life Skills Training',
      description: 'Practical workshops covering communication, time management, decision-making, and leadership.',
    },
    {
      icon: Award,
      title: 'Professional Development',
      description: 'Career coaching, resume building, interview preparation, and networking opportunities.',
    },
  ];

  const benefits = [
    'Build confidence and self-esteem through supportive relationships',
    'Develop practical skills for personal and professional success',
    'Access to a network of successful professionals and community leaders',
    'Learn from mentors who have overcome similar challenges',
    'Receive guidance on education, career paths, and life decisions',
    'Participate in group workshops and community events',
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="mentoring-page">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">
              Our Programs
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#0B1120] tracking-tight leading-tight mb-6">
              Mentoring Program
            </h1>
            <p className="text-xl text-slate-600 font-light leading-relaxed mb-8">
              Empowering individuals through meaningful connections. Our mentoring program pairs 
              those seeking guidance with experienced mentors who provide support, wisdom, and 
              encouragement for lasting personal and professional growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/')}
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-8 py-6 text-lg rounded-full"
                data-testid="mentoring-apply-btn"
              >
                Apply for Mentorship
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-2 border-slate-300 hover:border-[#1E3A8A] px-8 py-6 text-lg rounded-full"
              >
                Become a Mentor
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Features */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-4">
              What We Offer
            </h2>
            <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
              Our comprehensive mentoring program is designed to address every aspect of personal development.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programFeatures.map((feature, idx) => (
              <motion.div
                key={feature.title}
                className="p-8 rounded-2xl border border-slate-200 hover:border-[#1E3A8A]/30 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-[#DBEAFE] flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#1E3A8A]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 font-serif">
                  {feature.title}
                </h3>
                <p className="text-slate-600 font-light">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-6">
                Benefits of Our Mentoring Program
              </h2>
              <p className="text-lg text-slate-600 font-light mb-8">
                Whether you're a young person starting your journey or an adult seeking new direction, 
                our mentors provide the guidance and support you need to succeed.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <motion.div
                    key={idx}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80"
                alt="Mentoring session"
                className="rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1E3A8A] text-white p-6 rounded-xl shadow-lg">
                <p className="text-3xl font-bold font-serif">15,000+</p>
                <p className="text-sm text-white/80">Mentorship Hours</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-6">
              Ready to Transform Your Life?
            </h2>
            <p className="text-lg text-slate-600 font-light mb-8">
              Whether you're looking for guidance or want to share your experience as a mentor, 
              we'd love to hear from you.
            </p>
            <Button
              onClick={() => { navigate('/'); setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-10 py-6 text-lg rounded-full"
            >
              Get Started Today
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MentoringPage;
